import React, { useState, useEffect } from "react";
import {
  Typography,
  Space,
  Button,
  Row,
  Col,
  Spin,
  Alert,
  Tag,
  Layout,
  Breadcrumb,

} from "antd";
import { toast } from "react-toastify";
import {
  ArrowLeftOutlined,
  LeftOutlined,
  RightOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
  PlayCircleOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileZipOutlined,
  HomeOutlined
} from "@ant-design/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import usePageDetails from "PlatForms/SelfLMS/Hooks/Courses/usePageDetails";
import { selfLearnerService } from "PlatForms/SelfLMS/Services/Courses/CourseService";

// Configure PDF.js worker (bundled locally under /public so it works when the CDN is blocked)
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const { Title, Text } = Typography;
const { Content } = Layout;

const PageDetails = () => {
  const navigate = useNavigate();
  const { enrollmentId, pageId } = useParams();
  const currentPageId = parseInt(pageId) || 1;
  const location = useLocation();

  // PDF viewer states
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfTotalPages, setPdfTotalPages] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const moduleId = location.state?.moduleId;
  const [assessmentLoading, setAssessmentLoading] = useState(false);

  // Determine if this is an assessment page
  const isAssessmentPage = location.pathname.includes('/assessment');



  // Update the hook call to include isAssessment parameter
  const {
    pageData,
    fileContent,
    fileType,
    loading,
    error,
  } = usePageDetails(
    moduleId,
    isAssessmentPage ? null : currentPageId, // For assessment pages, pass null as pageId

    isAssessmentPage // This is the key parameter for assessment
  );
  const moduleProgressId = pageData?.moduleProgressId



  useEffect(() => {
    if (!moduleId) {
      console.error(' Module ID not provided');
      toast.error('Module information missing');
      handleBack();
    }
  }, [moduleId]);

  // Reset PDF viewer when page changes
  useEffect(() => {
    setPdfPageNumber(1);
    setPdfTotalPages(1);
  }, [pageId, isAssessmentPage]);

  // All your existing functions remain the same...
  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
      case 'image': return <FileImageOutlined style={{ color: '#52c41a' }} />;
      case 'document': return <FileWordOutlined style={{ color: '#9A6B0E' }} />;
      case 'spreadsheet': return <FileExcelOutlined style={{ color: '#52c41a' }} />;
      case 'archive': return <FileZipOutlined style={{ color: '#faad14' }} />;
      default: return <FileTextOutlined style={{ color: '#9A6B0E' }} />;
    }
  };

  const getFileTypeName = (type) => {
    switch (type) {
      case 'pdf': return 'PDF Document';
      case 'image': return 'Image File';
      case 'text': return 'Text File';
      case 'document': return 'Word Document';
      case 'spreadsheet': return 'Excel Spreadsheet';
      case 'archive': return 'Compressed Archive';
      default: return 'File';
    }
  };

  // PDF navigation functions
  const nextPdfPage = () => {
    if (pdfPageNumber < pdfTotalPages) {
      setPdfPageNumber(pdfPageNumber + 1);
    }
  };

  const prevPdfPage = () => {
    if (pdfPageNumber > 1) {
      setPdfPageNumber(pdfPageNumber - 1);
    }
  };

  const onPdfLoadSuccess = ({ numPages }) => {
    setPdfTotalPages(numPages);
    setPdfLoading(false);
  };

  const onPdfLoadStart = () => {
    setPdfLoading(true);
  };

  const renderFileContent = () => {
    if (!fileContent || !fileType) return null;

    switch (fileType) {
      case 'pdf':
        return (
          <div style={{ width: '100%' }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "20px",
                backgroundColor: "#F6F3EC",
                borderRadius: "8px",
                minHeight: "600px"
              }}
            >
              <div
                style={{
                  backgroundColor: "#fff",
                  maxWidth: "900px",
                  width: "100%",
                  overflow: "hidden",
                  border: "1px solid #e0e0e0",
                  marginBottom: "20px"
                }}
              >
                {fileContent && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      overflowX: "auto",
                      padding: "10px",
                    }}
                  >
                    <Document
                      file={fileContent}
                      onLoadSuccess={onPdfLoadSuccess}
                      onLoadStart={onPdfLoadStart}
                      loading={
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <Spin size="large" />
                          <div style={{ marginTop: '16px' }}>
                            <Text>Loading PDF document...</Text>
                          </div>
                        </div>
                      }
                    >
                      <Page
                        pageNumber={pdfPageNumber}
                        width={800}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                        loading={
                          <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Spin size="large" />
                            <div style={{ marginTop: '16px' }}>
                              <Text>Loading page {pdfPageNumber}...</Text>
                            </div>
                          </div>
                        }
                      />
                    </Document>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'image':
        return (
          <div style={{ textAlign: 'center' }}>
            <img
              src={fileContent}
              alt="Page content"
              style={{
                maxWidth: '100%',
                maxHeight: '500px',
                borderRadius: '8px',
              }}
            />
          </div>
        );

      case 'text':
        return (
          <div style={{ textAlign: 'center' }}>
            <Button
              type="primary"
              href={fileContent}
              target="_blank"
              icon={<FileTextOutlined />}
            >
              View Text File
            </Button>
          </div>
        );

      default:
        return (
          <div style={{ textAlign: 'center' }}>
            <Space direction="vertical" size="middle">
              {getFileIcon(fileType)}
              <Title level={4}>{getFileTypeName(fileType)}</Title>
              <Text>This file type is best viewed by downloading</Text>
              <div style={{ marginTop: '16px' }}>
                <Button
                  type="primary"
                  href={fileContent}
                  target="_blank"
                  download
                  icon={<FileTextOutlined />}
                >
                  Download File
                </Button>
              </div>
            </Space>
          </div>
        );
    }
  };

  const navigateToPage = (newPageId) => {
    if (newPageId >= 1) {
      const mpId = pageData?.moduleProgressId || moduleProgressId;

      navigate(`/selfLearner-dashboard/enrolled-courses/${enrollmentId}/modules/${mpId}/pages/${newPageId}`, {
        state: { moduleId }
      });
    }
  };

  const handleNextPage = () => {
    console.log(" handleNextPage - pageData:", {
      hasNext: pageData?.hasNext,
      nextPageId: pageData?.nextPageId,
      nextIsAssessment: pageData?.nextIsAssessment,
      isAssessmentPage: pageData?.isAssessmentPage,
      assessmentPage: pageData?.assessmentPage
    });

    if (pageData?.nextIsAssessment) {
      navigateToAssessment();
    } else if (pageData?.hasNext && pageData.nextPageId) {
      navigateToPage(pageData.nextPageId);
    } else {
      handleCompleteModule();
    }
  };

  const handlePreviousPage = () => {
    if (pageData?.hasPrevious && pageData.previousPageId) {
      navigateToPage(pageData.previousPageId);
    }
  };



  const navigateToAssessment = async () => {
    try {
      setAssessmentLoading(true);

      // First, fetch the assessment data to get the assessment ID

      const assessmentData = await selfLearnerService.getLearnerPageData(moduleId, null, true);

      // Extract the assessment ID from the response
      const assessmentId = assessmentData?.assessmentPage?.id;

      if (!assessmentId) {
        console.error(" No assessment ID found in response");
        toast.error("Assessment not available");
        setAssessmentLoading(false);
        return;
      }


      // Now navigate to the assessment page with the assessment ID

      navigate(`/selfLearner-dashboard/enrolled-courses/${enrollmentId}/modules/${moduleProgressId}/assessment`, {
        state: {
          moduleId: moduleId,
          assessmentId: assessmentId,
          moduleProgressId: moduleProgressId,
          enrollmentId: enrollmentId,
          assessmentTitle: assessmentData.assessmentPage?.title,
          assessmentData: assessmentData
        }
      });

    } catch (error) {
      console.error(" Failed to navigate to assessment:", error);
      toast.error("Failed to load assessment");
      setAssessmentLoading(false);
    }
  };

  // Update the renderNavigationButtons function
  const renderNavigationButtons = () => {
    const isOnAssessmentPage = pageData?.isAssessmentPage;

    console.log(" Navigation buttons - Current state:", {
      isOnAssessmentPage,
      nextIsAssessment: pageData?.nextIsAssessment,
      hasNext: pageData?.hasNext,
      nextPageId: pageData?.nextPageId
    });

    return (
      <>



        <Col>
          <Space>
            {/* When we're ON the assessment page preview */}
            {isOnAssessmentPage ? (
              <Button
                type="primary"
                onClick={navigateToAssessment}
                size="large"
                icon={<CheckSquareOutlined />}
                loading={assessmentLoading}
              >
                {assessmentLoading ? "Loading..." : "Take Assessment"}
              </Button>
            ) :
              /* When assessment is the next item (last content page) */
              pageData?.nextIsAssessment ? (
                <Button
                  type="primary"
                  onClick={navigateToAssessment}
                  size="large"
                  icon={<RightOutlined />}
                  loading={assessmentLoading}
                >
                  {assessmentLoading ? "Loading..." : "Start Assessment"}
                </Button>
              ) :
                /* Regular next page */
                pageData?.hasNext && pageData.nextPageId ? (
                  <Button
                    type="primary"
                    onClick={handleNextPage}
                    size="large"
                    icon={<RightOutlined />}
                    disabled={assessmentLoading}
                  >
                    Next Page
                  </Button>
                ) : (
                  /* No more pages */
                  <Button
                    type="primary"
                    onClick={handleCompleteModule}
                    size="large"
                    icon={<PlayCircleOutlined />}
                    disabled={assessmentLoading}
                  >
                    Complete Module
                  </Button>
                )}
          </Space>
        </Col>
      </>
    );
  };



  const handleCompleteModule = () => {
    toast.success("Module completed successfully!");
    handleBack();
  };

  const handleBack = () => {
    navigate(`/selfLearner-dashboard/enrolled-courses/courses/${enrollmentId}`);
  };



  const formatTime = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "Not specified";

    const [hours, minutes] = timeString.split(":");
    const hoursInt = parseInt(hours);
    const minutesInt = parseInt(minutes);

    if (hoursInt === 0 && minutesInt === 0) return "Not specified";
    if (hoursInt === 0) return `${minutesInt}m`;
    if (minutesInt === 0) return `${hoursInt}h`;

    return `${hoursInt}h ${minutesInt}m`;
  };

  const getProgressPercentage = () => {
    if (!pageData?.totalPages) return 0;
    return pageData.progressPercentage || 0;
  };

  const getCurrentDisplayNumber = () => {
    return pageData?.currentPage?.pageNumber || currentPageId;
  };

  const getTotalDisplayPages = () => {
    const total = pageData?.totalPages || 0;
    if (total <= 0) return 0;
    // totalPages includes the assessment page; subtract it only when the module has an assessment
    const hasAssessment =
      pageData?.nextIsAssessment ||
      pageData?.isAssessmentPage ||
      !!pageData?.assessmentPage;
    const contentPages = hasAssessment ? total - 1 : total;
    return Math.max(contentPages, 1);
  };

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (fileContent) {
        URL.revokeObjectURL(fileContent);
      }
    };
  }, [fileContent]);

  // Debug effect
  useEffect(() => {
  }, [pageData, isAssessmentPage, loading]);

  if (loading && !pageData) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Page"
        description={error}
        type="error"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={handleBack}>
            Go Back
          </Button>
        }
      />
    );
  }

  if (!pageData) {
    return (
      <Alert
        message="Page Not Found"
        description="The requested page could not be found."
        type="error"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={handleBack}>
            Go Back
          </Button>
        }
      />
    );
  }

  const renderContentArea = () => {
    // If we're on the assessment page
    if (pageData?.isAssessmentPage || isAssessmentPage) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <CheckSquareOutlined style={{ fontSize: '64px', color: '#ff4d4f', marginBottom: '24px' }} />
          <Title level={3}>Final Assessment</Title>
          <Text style={{ fontSize: '16px', display: 'block', marginBottom: '16px' }}>
            {pageData.assessmentPage?.title || "Module Assessment"}
          </Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
            Estimated Time: {formatTime(pageData.assessmentPage?.estimatedTime)}
          </Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
            This is the final assessment for this module
          </Text>
          <Button
            type="primary"
            size="large"
            onClick={navigateToAssessment}
            icon={<CheckSquareOutlined />}
          >
            Start Assessment
          </Button>
        </div>
      );
    }

    // Regular page content
    return (
      <div>
        {fileContent ? (
          <div>
            {renderFileContent()}
          </div>
        ) : pageData.currentPage?.htmlContent ? (
          <div
            dangerouslySetInnerHTML={{ __html: pageData.currentPage.htmlContent }}
            style={{
              maxWidth: '100%',
              lineHeight: '1.6',
              fontSize: '16px'
            }}
          />
        ) : pageData.currentPage?.text ? (
          <Text style={{ fontSize: '16px', lineHeight: '1.6' }}>
            {pageData.currentPage.text}
          </Text>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
            <Title level={4} type="secondary">No Content Available</Title>
            <Text type="secondary">
              This page exists but has no displayable content.
            </Text>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#F6F3EC" }}>
      <Content style={{ padding: "0" }}>
        {/* Breadcrumb */}
        <div style={{ background: "#fff", borderBottom: "1px solid #e8e8e8", padding: "16px 0" }}>
          <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px" }}>
            <Breadcrumb
              items={[
                { title: <HomeOutlined />, onClick: () => navigate('/selfLearner-dashboard') },
                { title: 'Courses', onClick: () => navigate('/selfLearner-dashboard/enrolled-courses') },
                { title: pageData?.module?.title || 'Module', onClick: handleBack },
                { title: isAssessmentPage ? 'Assessment' : `Page ${getCurrentDisplayNumber()}` }
              ]}
            />
          </div>
        </div>


        {/* Main Content */}
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "24px" }}>
          <div style={{
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            overflow: "hidden"
          }}>
            {/* Header */}
            <div style={{
              padding: "24px",
              borderBottom: "1px solid #e8e8e8",
              background: "linear-gradient(135deg, #FFFDF8 0%, #F5EFE2 100%)"
            }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Space direction="vertical" size="small">
                    <Title level={2} style={{ margin: 0, fontSize: "28px", color: "#323130" }}>
                      {isAssessmentPage ? 'Assessment' : pageData?.currentPage?.title || `Page ${getCurrentDisplayNumber()}`}
                    </Title>
                    <Space>
                      <Tag color="gold" icon={<ClockCircleOutlined />}>
                        {formatTime(pageData?.currentPage?.estimatedTime || pageData?.assessmentPage?.estimatedTime)}
                      </Tag>
                      {pageData?.currentPage?.isCompleted && (
                        <Tag color="green" icon={<CheckSquareOutlined />}>
                          Completed
                        </Tag>
                      )}
                    </Space>
                  </Space>
                </Col>
                <Col>
                  <Button
                    type="default"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBack}
                    size="small"
                  >
                    Back to Module
                  </Button>

                </Col>
              </Row>
            </div>

            {/* Content */}
            <div style={{ padding: "32px", minHeight: "500px" }}>
              {loading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
                  <Spin size="large" />
                </div>
              ) : (
                <div style={{ lineHeight: "1.6", fontSize: "16px" }}>
                  {renderContentArea()}
                </div>
              )}
            </div>

            {/* Footer Navigation */}
            <div style={{
              padding: "14px 24px",
              borderTop: "1px solid #E7E1D5",
              background: "rgba(255, 253, 248, 0.95)",
              backdropFilter: "blur(6px)",
              position: "sticky",
              bottom: 0,
              zIndex: 5,
              boxShadow: "0 -6px 18px rgba(41, 37, 31, 0.06)"
            }}>
              <Row justify="space-between" align="middle">
                <Col>
                  {pageData?.hasPrevious && (
                    <Button
                      type="default"
                      icon={<LeftOutlined />}
                      onClick={handlePreviousPage}
                      size="large"
                    >
                      Previous
                    </Button>
                  )}
                </Col>
                <Col>
                  <Space>
                    <Text type="secondary">
                      {isAssessmentPage
                        ? 'Assessment'
                        : getTotalDisplayPages() > 0
                          ? `Page ${getCurrentDisplayNumber()} of ${getTotalDisplayPages()}`
                          : `Page ${getCurrentDisplayNumber()}`}
                    </Text>
                  </Space>
                </Col>
                <Col>
                  {renderNavigationButtons()}
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default PageDetails;